import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Mail, KeyRound, Lock, ArrowRight, CheckCircle2 } from "lucide-react";
import BrandLogo from "../components/BrandLogo";
import BrandAttribution from "../components/BrandAttribution";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator,
} from "../components/ui/input-otp";
import api, { formatApiError } from "../lib/api";

/**
 * Step 2 of OTP-based password reset.
 *
 * Required fields: email, 6-digit OTP, new password (+ confirm).
 * The form gates the password input until the OTP is fully entered,
 * so the user can't accidentally submit a half-typed code.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const initialEmail = params.get("email") || "";
  const [email, setEmail] = useState(initialEmail);
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [success, setSuccess] = useState(false);
  const nav = useNavigate();

  // Focus the OTP input automatically once the email is set.
  useEffect(() => {
    if (initialEmail) {
      const el = document.querySelector('[data-testid="reset-otp-input"] input');
      if (el) el.focus();
    }
  }, [initialEmail]);

  const otpComplete = otp.length === 6;
  const passwordsMatch = password === confirm && password.length >= 6;
  const canSubmit = email.trim() && otpComplete && passwordsMatch && !submitting;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", {
        email: email.trim(),
        otp,
        new_password: password,
      });
      setSuccess(true);
      toast.success("Password updated. Sign in with your new password.");
      setTimeout(() => nav("/login"), 1500);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
    setSubmitting(false);
  };

  const resend = async () => {
    if (!email.trim() || resending) return;
    setResending(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      toast.success("A fresh code is on the way.");
      setOtp("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
    setResending(false);
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-[#f6f8fb]" data-testid="reset-success">
        <div className="w-full max-w-md rounded-xl bg-white border border-slate-200 p-8 shadow-sm text-center">
          <CheckCircle2 className="h-14 w-14 mx-auto text-emerald-500" />
          <h1 className="mt-6 text-2xl font-bold text-[#0A192F]">Password updated</h1>
          <p className="mt-2 text-sm text-slate-500">Redirecting to sign in…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-[#f6f8fb]">
      <div className="w-full max-w-md rounded-xl bg-white border border-slate-200 p-8 shadow-sm">
        <BrandLogo />
        <h1 className="mt-8 text-3xl font-bold tracking-tighter text-[#0A192F]">Set a new password</h1>
        <p className="mt-2 text-sm text-slate-500">
          Enter the 6-digit code we emailed to your inbox, then pick a strong new password.
        </p>
        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="reset-form">

          <div>
            <Label className="text-xs font-semibold">Email</Label>
            <div className="mt-2 relative">
              <Mail className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 pl-9"
                placeholder="you@school.org"
                data-testid="reset-email-input"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs font-semibold flex items-center gap-2">
              <KeyRound className="h-3 w-3 text-[#D4AF37]" /> 6-digit code
            </Label>
            <div className="mt-2" data-testid="reset-otp-input">
              <InputOTP
                maxLength={6}
                value={otp}
                onChange={(v) => setOtp(v.replace(/\D/g, ""))}
                containerClassName="justify-center gap-3"
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} className="h-12 w-10 text-lg" />
                  <InputOTPSlot index={1} className="h-12 w-10 text-lg" />
                  <InputOTPSlot index={2} className="h-12 w-10 text-lg" />
                </InputOTPGroup>
                <InputOTPSeparator />
                <InputOTPGroup>
                  <InputOTPSlot index={3} className="h-12 w-10 text-lg" />
                  <InputOTPSlot index={4} className="h-12 w-10 text-lg" />
                  <InputOTPSlot index={5} className="h-12 w-10 text-lg" />
                </InputOTPGroup>
              </InputOTP>
            </div>
            <p className="mt-2 text-[11px] text-slate-400 text-center">
              Didn&apos;t get it?{" "}
              <button
                type="button"
                onClick={resend}
                disabled={resending || !email.trim()}
                className="text-[#0F4C81] font-semibold hover:underline disabled:opacity-50"
                data-testid="reset-resend-button"
              >
                {resending ? "Resending…" : "Resend"}
              </button>
            </p>
          </div>

          <div>
            <Label className="text-xs font-semibold flex items-center gap-2">
              <Lock className="h-3 w-3 text-[#D4AF37]" /> New password
            </Label>
            <Input
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 h-12"
              placeholder="Min 6 characters"
              disabled={!otpComplete}
              data-testid="reset-password-input"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold">Confirm new password</Label>
            <Input
              type="password"
              required
              minLength={6}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="mt-2 h-12"
              placeholder="Type it again"
              disabled={!otpComplete}
              data-testid="reset-confirm-input"
            />
            {confirm && password !== confirm && (
              <p className="mt-1 text-xs text-red-500" data-testid="reset-mismatch">
                Passwords don&apos;t match.
              </p>
            )}
          </div>

          <Button
            type="submit"
            disabled={!canSubmit}
            className="w-full h-12 bg-[#0F4C81] hover:bg-[#0A3860] gap-2 disabled:opacity-50"
            data-testid="reset-submit-button"
          >
            {submitting ? "Saving…" : "Reset password"}
            {!submitting && <ArrowRight className="h-4 w-4" />}
          </Button>
        </form>
        <p className="mt-6 text-sm text-slate-500">
          <Link to="/login" className="text-[#0F4C81] font-semibold hover:underline" data-testid="back-to-login-link">
            Back to sign in
          </Link>
        </p>
        <BrandAttribution className="mt-8" />
      </div>
    </div>
  );
}
